import React, { useState, useEffect } from "react";
import { useNavigate, Outlet, useParams } from "react-router-dom";
import PostedJobsComponent from "./PostedJobsComponent";
import ClientMessagesContent from "../messages_area/ClientMessagesComponent";
import ClientAgreementsContent from "../agreements_Area/AdminClientAgreementsComponent";
import WorkerClaimsInvoicesBillingComponent from "../worker-user-billing/WorkerClaimsInvoicesBillingComponent";
import ActivityLogsComponent from "../../../activity_logs/ActivityLogsComponent";
import config from "../../../config/config";
import axios from "axios";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaRegIdCard,
  FaBriefcase,
  FaMoneyBillAlt,
  FaHeart,
  FaTrashAlt,
  FaBan,
  FaUnlockAlt,
  FaDollarSign,
} from "react-icons/fa";
import { TbHistory } from "react-icons/tb";
import { TiMessages } from "react-icons/ti";
import { MdLockReset } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { IoMdArrowRoundBack } from "react-icons/io";
import { LiaFileContractSolid } from "react-icons/lia";
import { IoPersonCircle } from "react-icons/io5";

const UserDetails = ({
  user,
  onEdit,
  onDeactivate,
  onActivate,
  onDelete,
  toggleLeft,
}) => {
  const baseURL = config.API_BASE_URL;
  const navigate = useNavigate();
  const [activeContent, setActiveContent] = useState(null);
  const [fade, setFade] = useState("fade-in");

  useEffect(() => {
    if (activeContent) {
      setFade("fade-out");
      const timeoutId = setTimeout(() => setFade("fade-in"), 300);
      return () => clearTimeout(timeoutId);
    }
  }, [activeContent]);

  useEffect(() => {
    setActiveContent(null);
  }, [user]);

  if (!user) {
    return (
      <div className="welcome-container">
        <div className="welcome-content">
          <h1>Welcome to the Worker User Management Portal</h1>
          <div className="guidance-text">
            Use this portal to search for worker users and select them to view
            more details. Navigate through the options to manage and update
            worker user information efficiently.
          </div>
        </div>
      </div>
    );
  }

  const profileImageUrl = user?.profilePhoto
    ? `${baseURL}/v1/worker-users/profile-images/${user.profilePhoto}`
    : "/images/avatar.jpg";

  const handleResetPassword = async (user) => {
    try {
      const response = await axios.post(
        `${baseURL}/v1/worker-users/reset-password/${user.id}`
      );
      if (response.data) {
        console.log("Password reset successfully for user:", user.email);
        alert(
          "Password reset successfully. The new password has been sent to the user's email."
        );
      } else {
        console.error("Unexpected response status:", response.status);
        alert("Failed to reset password. Please try again later.");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      alert("Failed to reset password. Please try again later.");
    }
  };

  const addInlineStyles = (htmlContent) => {
    const fontStyle = "font-family: 'Quicksand', sans-serif;";

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    const elements = doc.body.querySelectorAll("*");
    elements.forEach((element) => {
      element.setAttribute("style", fontStyle);
    });

    const updatedHtmlContent = doc.body.innerHTML;

    return updatedHtmlContent;
  };

  const handleNavigation = (path) => {
    setActiveContent(path);
  };

  const calculateAge = (dateOfBirth) => {
    const dob = new Date(dateOfBirth);
    const diffMs = Date.now() - dob.getTime();
    const ageDt = new Date(diffMs);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  };

  const renderContent = () => {
    switch (activeContent) {
      case "posted-jobs":
        return <PostedJobsComponent userId={user.id} />;
      case "messages":
        return <ClientMessagesContent userId={user.id} />;
      case "agreements":
        return <ClientAgreementsContent user={user} toggleLeft={toggleLeft} />;
      case "billing":
        return (
          <WorkerClaimsInvoicesBillingComponent
            user={user}
            toggleLeft={toggleLeft}
          />
        );
      case "invoices":
        return <div>Invoices Content</div>;
      case "activityLog":
        return <ActivityLogsComponent user={user} />;
      default:
        return (
          <div className="user-details-content">
            <div className="user-details-header">
              <div className="user-details-header-inner">
                <div className="user-details-profile-photo">
                  <img
                    src={profileImageUrl}
                    alt={`${user.firstName} ${user.lastName}`}
                  />
                </div>
                <div className="user-basic-info">
                  <h2>
                    {user.firstName} {user.lastName}
                  </h2>
                  <p>
                    <FaEnvelope className="user-details-icon-email" />
                    {user.email}
                  </p>
                  <p>
                    <FaPhone className="user-details-icon-phone" />
                    {user.mobileNumber}
                  </p>
                  <p>
                    <FaMapMarkerAlt className="user-details-icon-place" />
                    {user.primaryLocation}
                  </p>
                </div>
              </div>
              <div>
                <div className="user-actions">
                  <button
                    className="default"
                    onClick={() => handleNavigation(`posted-jobs`)}
                  >
                    <FaBriefcase /> Jobs Posted
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("messages")}
                  >
                    <TiMessages /> Messages
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("agreements")}
                  >
                    <LiaFileContractSolid /> Agreements
                  </button>
                  <button
                    className="default-billing"
                    onClick={() => setActiveContent("billing")}
                  >
                    <FaDollarSign /> Claims - Invoices - Payments
                  </button>
                </div>
                <div className="user-actions">
                  <button className="edit" onClick={() => onEdit(user)}>
                    <CiEdit /> Edit User
                  </button>
                  <button
                    className="reset"
                    onClick={() => handleResetPassword(user)}
                  >
                    <MdLockReset /> Reset Password
                  </button>
                  {user.userLocked ? (
                    <button
                      className="deactivate"
                      onClick={() => onDeactivate(user)}
                    >
                      <FaBan /> Unlock User
                    </button>
                  ) : (
                    <button
                      className="activate"
                      onClick={() => onActivate(user)}
                    >
                      <FaUnlockAlt /> Lock User
                    </button>
                  )}
                  <button className="delete" onClick={() => onDelete(user)}>
                    <FaTrashAlt /> Delete
                  </button>
                  <button
                    className="default"
                    onClick={() => setActiveContent("activityLog")}
                  >
                    <TbHistory /> Activity Log
                  </button>
                </div>
              </div>
            </div>
            <div className="user-details-section">
              <h3>
                <IoPersonCircle /> About Client
              </h3>
              <div
                className="user-details-bio"
                dangerouslySetInnerHTML={{
                  __html: addInlineStyles(user.about || "N/A"),
                }}
              ></div>
            </div>
            <div className="user-details-section">
              <h3>
                <FaRegIdCard /> Personal Information
              </h3>
              <ul>
                <li>
                  <strong>Gender &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.gender || "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Date Of Birth &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.dateOfBirth || "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Age &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {calculateAge(user.dateOfBirth) || "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Above 18 &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.isAbove18 ? "Yes" : "No"}
                  </div>
                </li>
                <li>
                  <strong>Email Verified &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.isEmailVerified ? "Yes" : "No"}
                  </div>
                </li>
                <li>
                  <strong>Phone Number Verified &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.isPhoneNumberVerified ? "Yes" : "No"}
                  </div>
                </li>
                <li>
                  <strong>Contact Details Updated &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.contactDetailsUpdated ? "Yes" : "No"}
                  </div>
                </li>
                <li>
                  <strong>Personal Details Updated &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.personalDetailsUpdated ? "Yes" : "No"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaBriefcase /> Support Type
              </h3>
              <ul>
                <li>
                  <strong>
                    Support Type &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.supportType && user.supportType.length > 0
                      ? user.supportType.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>
                    Pay Option &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.payOption && user.payOption.length > 0
                      ? user.payOption.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>
                    Fund Manage &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.fundManage || "N/A"}
                  </div>
                </li>
                <li>
                  <strong>
                    Time Estimate &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.timeEstimate || "N/A"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaMoneyBillAlt /> Payment Details
              </h3>
              <ul>
                <li>
                  <strong>
                    Using Government Funding &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.paymentDetails.usingGovernmentFunding ? "Yes" : "No"}
                  </div>
                </li>
                <li>
                  <strong>
                    Payment Type &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user?.paymentDetails?.usingGovernmentFunding
                      ? "Yes"
                      : "No"}
                  </div>
                </li>
                <li>
                  <strong>
                    Payment Method &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.paymentDetails.paymentIntentDetails.paymentMethodId ||
                      "N/A"}
                  </div>
                </li>
                <li>
                  <strong>
                    Postal Code &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.paymentDetails.paymentIntentDetails.postalCode ||
                      "N/A"}
                  </div>
                </li>
                <li>
                  <strong>
                    Country &nbsp;
                    <span className="user-detail-arrow">➤</span>
                  </strong>
                  <div className="userdetails-section-item">
                    {user.paymentDetails.paymentIntentDetails.country || "N/A"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaHeart /> Interests and Hobbies
              </h3>
              <ul>
                {user.interestsAndHobbies.length > 0 ? (
                  user.interestsAndHobbies.map((hobby, index) => (
                    <li key={index}>{hobby}</li>
                  ))
                ) : (
                  <li>N/A</li>
                )}
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaBriefcase /> Emergency Contacts
              </h3>
              {user.emergencyContacts.length > 0 ? (
                user.emergencyContacts.map((contact, index) => (
                  <div key={index} className="user-details-education-section">
                    <ul>
                      <li>
                        <strong>Relationship &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {contact.relationship}
                        </div>
                      </li>
                      <li>
                        <strong>First Name &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {contact.firstName}
                        </div>
                      </li>
                      <li>
                        <strong>Last Name &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {contact.lastName}
                        </div>
                      </li>
                      <li>
                        <strong>Contact Phone &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {contact.contactPhone}
                        </div>
                      </li>
                      <li>
                        <strong>Date Of Birth &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {contact.dateOfBirth}
                        </div>
                      </li>
                    </ul>
                  </div>
                ))
              ) : (
                <li>N/A</li>
              )}
            </div>
            <div className="user-details-section">
              <h3>
                <FaRegIdCard /> Languages
              </h3>
              <ul>
                <li>
                  <strong>Main Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages.mainLanguages &&
                    user.languages.mainLanguages.length > 0
                      ? user.languages.mainLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
                <li>
                  <strong>Other Languages &nbsp; </strong>&nbsp;
                  <span className="user-detail-arrow">➤</span>
                  <div className="userdetails-section-item">
                    {user.languages.otherLanguages &&
                    user.languages.otherLanguages.length > 0
                      ? user.languages.otherLanguages.join(", ")
                      : "N/A"}
                  </div>
                </li>
              </ul>
            </div>
            <div className="user-details-section">
              <h3>
                <FaDollarSign /> Account Manager Details
              </h3>
              {user.accountManagerDetails.length > 0 ? (
                user.accountManagerDetails.map((manager, index) => (
                  <div key={index} className="user-details-education-section">
                    <ul>
                      <li>
                        <strong>Relationship &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {manager.relationship}
                        </div>
                      </li>
                      <li>
                        <strong>First Name &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {manager.firstName}
                        </div>
                      </li>
                      <li>
                        <strong>Last Name &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {manager.lastName}
                        </div>
                      </li>
                      <li>
                        <strong>Contact Phone &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {manager.contactPhone}
                        </div>
                      </li>
                      <li>
                        <strong>Date Of Birth &nbsp;</strong>&nbsp;
                        <span className="user-detail-arrow">➤</span>
                        <div className="userdetails-section-item">
                          {manager.dateOfBirth}
                        </div>
                      </li>
                    </ul>
                  </div>
                ))
              ) : (
                <li>N/A</li>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div>
      <div className="user-details-close-button-container">
        {activeContent && (
          <button
            className="user-details-close-button"
            onClick={() => {
              setActiveContent(null);
              navigate(
                `/account/admin-dashboard/users/client-users/${user.id}`
              );
              if (toggleLeft) {
                toggleLeft(false);
              }
            }}
          >
            <IoMdArrowRoundBack className="user-details-back-icon" /> User
            Details
          </button>
        )}
      </div>

      <div className="user-details-content-container">{renderContent()}</div>
    </div>
  );
};

export default UserDetails;
